/**
 * What the Ask ResAI error banner says when a chat turn fails (PRD-1531).
 *
 * A timeout gets its own line because "try rephrasing" is the wrong advice for
 * it: the stalls seen in prod were transient, and the same question went
 * through on a second try.
 */
export const TIMEOUT_NOTICE = 'ResAI took too long to respond. Please try again.';
export const SEND_FAILED_NOTICE = 'Failed to send message. Please try again.';
export const AGENT_ERROR_NOTICE =
  'The AI agent encountered an error processing your request. Please try rephrasing your message.';

// One question at a time per conversation. The composer says so while an
// answer is generating; the agent refuses a second run with code "thread_busy"
// (409) in case one slips through, and the question goes back in the box.
export const STILL_ANSWERING_HINT = 'ResAI is still answering. You can send this when the answer arrives.';
export const THREAD_BUSY_CODE = 'thread_busy';
export const THREAD_BUSY_NOTICE =
  'ResAI is still answering your previous question. Your message is back in the box: send it when the answer arrives.';
export const THREAD_BUSY_RETRY_NOTICE =
  'ResAI is still answering your previous question. Try again when the answer arrives.';

/** Whether a banner is the busy notice, which is stale once the earlier answer arrives. */
export function isBusyNotice(notice) {
  return notice === THREAD_BUSY_NOTICE || notice === THREAD_BUSY_RETRY_NOTICE;
}

/** Whether a failed send was refused because the conversation is still answering. */
export function isThreadBusy(err) {
  // The code only, never a bare 409: the send routes return other 409s too (an empty
  // drill-down scope), and reading one as "busy" would loop the user on a retry.
  return err?.code === THREAD_BUSY_CODE;
}

// Only `/state` classifies a failure (`error.kind`). The live stream still
// carries raw text: "The request took too long and was stopped..." from the
// agent, or `StreamChunkTimeoutError(...)` in the done payload. Match the
// meaning rather than one exact string, so a reworded message still reads as a
// timeout.
const TIMEOUT_PATTERN = /timeout|timed out|took too long/i;

/**
 * @param {*} reason the error text the backend gave, if any
 * @param {string} fallback the notice to show when the reason is not a timeout
 * @returns {string}
 */
export function turnFailureNotice(reason, fallback) {
  return TIMEOUT_PATTERN.test(String(reason ?? '')) ? TIMEOUT_NOTICE : fallback;
}

/**
 * Why the last run failed, from a `/threads/{id}/state` payload, or null.
 * Reads the top-level `error.message`, and falls back to the first failed
 * task's error for a resapi that does not send it yet.
 */
export function taskErrorOf(state) {
  if (state?.error?.message) return state.error.message;
  const failed = state?.tasks?.find((t) => t?.error);
  return failed ? failed.error : null;
}

/**
 * The banner notice for a failed run in a `/threads/{id}/state` payload, or
 * null when the last run did not fail. Switches on resapi's `error.kind`, and
 * reads the error text only for a resapi that does not classify it yet.
 */
export function stateFailureNotice(state) {
  const kind = state?.error?.kind;
  if (kind) return kind === 'timeout' ? TIMEOUT_NOTICE : AGENT_ERROR_NOTICE;
  const reason = taskErrorOf(state);
  return reason ? turnFailureNotice(reason, AGENT_ERROR_NOTICE) : null;
}
